import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { IonIcon } from '@ionic/react';
import { close, ribbon, school } from 'ionicons/icons';
import type { OfoDescription } from '@/lib/ofo-client-search';

interface SkillDetailPanelProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  detail: OfoDescription | null;
  skillName: string;
}

const SKILL_LEVEL_LABEL: Record<string, string> = {
  Low: 'Entry level',
  Medium: 'Mid level',
  High: 'Professional',
};

export function SkillDetailPanel({ open, onClose, loading, detail, skillName }: SkillDetailPanelProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        className={`bp-skill-scrim ${open ? 'is-open' : ''}`}
        onClick={onClose}
        aria-hidden={!open}
      />
      <aside
        className={`bp-skill-panel ${open ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={`About ${skillName}`}
      >
        <header className="bp-skill-panel-head">
          <div className="bp-skill-panel-head-main">
            <span className="bp-skill-panel-eyebrow">Skills &amp; expertise</span>
            <h2 className="bp-skill-panel-title">{skillName}</h2>
            {detail ? (
              <div className="bp-skill-panel-crumb">
                {detail.minorGroup && <span>{detail.minorGroup}</span>}
                {detail.unitGroup && (
                  <>
                    <span className="bp-skill-panel-crumb-sep">›</span>
                    <span>{detail.unitGroup}</span>
                  </>
                )}
              </div>
            ) : null}
          </div>
          <button type="button" className="bp-skill-panel-close" onClick={onClose} aria-label="Close">
            <IonIcon icon={close} />
          </button>
        </header>

        <div className="bp-skill-panel-body">
          {loading && (
            <div className="bp-skill-panel-loading">
              <span className="bp-spinner" />
              Loading occupation details…
            </div>
          )}

          {!loading && detail && (
            <>
              {detail.title && detail.title.toLowerCase() !== skillName.trim().toLowerCase() && (
                <div className="bp-skill-panel-formal">
                  <IonIcon icon={ribbon} />
                  Matches occupation: <strong>{detail.title}</strong>
                </div>
              )}

              {detail.generatedDescription ? (
                <div className="bp-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.generatedDescription}</ReactMarkdown>
                </div>
              ) : (
                <p className="bp-skill-panel-empty">
                  No detailed description is available for this occupation yet.
                </p>
              )}

              {detail.skillLevel && (
                <div className="bp-skill-panel-meta">
                  <IonIcon icon={school} />
                  <span>{SKILL_LEVEL_LABEL[detail.skillLevel] ?? detail.skillLevel} skill level</span>
                </div>
              )}

              {detail.alternateTitles?.length ? (
                <div className="bp-skill-panel-aliases">
                  <span className="bp-skill-panel-aliases-label">Also known as</span>
                  <div className="bp-skill-panel-aliases-row">
                    {detail.alternateTitles.map((a) => (
                      <span key={a} className="bp-skill-chip">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}

          {!loading && !detail && (
            <p className="bp-skill-panel-empty">
              We couldn’t find an occupation description for “{skillName}”.
            </p>
          )}
        </div>
      </aside>
    </>
  );
}
